/**
 * Helper to construct customer portal form links.
 *
 * Rules:
 * 1. Customer portal URL points to findlocalpetservices.com (NOT vendor.findlocalpetservices.com)
 * 2. On localhost, customer portal runs on port 3001 (NOT port 3000)
 */
import config from "../config"

export const getCustomerPortalBaseUrl = () => {
  let url = "";

  // Check env variable (if set and not pointing to stale 3000)
  if (config.CUSTOMER_APP_BASE_URL || process.env.REACT_APP_CUSTOMER_VIEW) {
    url = config.CUSTOMER_APP_BASE_URL || process.env.REACT_APP_CUSTOMER_VIEW.trim().replace(/\/$/, "");
  }
  
  if (!url && typeof window !== "undefined" && window.location) {
    url = window.location.origin;
  }

  if (!url) {
    url = "https://findlocalpetservices.com";
  }

  // 1. Force replace local port: 3000 -> 3001
  if (url.includes(":3000")) {
    url = url.replace(":3000", ":3001");
  }

  // 2. Force replace production domain: vendor.findlocalpetservices.com -> findlocalpetservices.com
  if (url.includes("vendor.findlocalpetservices.com")) {
    url = url.replace("vendor.findlocalpetservices.com", "findlocalpetservices.com");
  } else if (url.includes("://vendor.")) {
    url = url.replace("://vendor.", "://");
  }

  // 3. If running on localhost without explicit port
  if (url === "http://localhost" || url === "https://localhost") {
    url = "http://localhost:3001";
  }

  return url;
};

/**
 * Builds the complete customer-facing form link.
 *
 * @param {string} vendorId
 * @param {string} formId
 * @param {string} existingFormLink (optional)
 * @param {string} query (optional, e.g. "orderId=123")
 * @returns {string}
 */
export const buildFormPublicLink = (vendorId, formId, existingFormLink, query = "") => {
  const baseUrl = getCustomerPortalBaseUrl();
  const queryString = query ? (query.startsWith("?") ? query : `?${query}`) : "";

  // If vendorId and formId are available, construct directly from clean baseUrl
  if (vendorId && formId) {
    return `${baseUrl}/form/${vendorId}/${formId}${queryString}`;
  }

  if (existingFormLink) {
    try {
      let normalized = String(existingFormLink)
        .replace(":3000", ":3001")
        .replace("vendor.findlocalpetservices.com", "findlocalpetservices.com")
        .replace("://vendor.", "://");

      if (normalized.startsWith("/")) {
        normalized = `${baseUrl}${normalized}`;
      }

      if (queryString) {
        const url = new URL(normalized);
        const searchParams = new URLSearchParams(queryString.replace(/^\?/, ""));
        searchParams.forEach((val, key) => url.searchParams.set(key, val));
        return url.toString();
      }
      return normalized;
    } catch {
      // Fallback
    }
  }

  return `${baseUrl}/form/preview${queryString}`;
};
